import { mockInvoicesByCustomerId, mockPaymentsByCustomerId } from '@/lib/demo-data/customer-transactions';
import { mapCustomerDetailApiToUi, mapInvoiceApiToUi, mapPaymentApiToUi } from '@/lib/mappers/customer-detail';
import { getCustomerById as getMockCustomerById } from '@/lib/mock-customers';
import { getCustomerActivities as getMockCustomerActivities } from '@/mockData';
import type { CustomerActivity } from '@/mockData/customerActivity';
import type {
  CustomerDetailApi,
  OutstandingInvoicesApiResponse,
  PaymentHistoryApiResponse,
  StubResponse,
} from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import type { GetRecentActivitiesApiResponse, RecentActivityListItemApiResponse } from '@/types/api/recent-activities';
import type { CustomerDetailUI, InvoiceUI, PaymentUI } from '@/types/ui/customer-detail';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

/**
 * Convert mock customer to CustomerDetailUI format
 * Mock customers have most fields, but need to add the extra fields for detail view
 */
function convertMockCustomerToDetailUI(mockCustomer: ReturnType<typeof getMockCustomerById>): CustomerDetailUI | null {
  if (!mockCustomer) return null;

  return {
    id: mockCustomer.id,
    name: mockCustomer.name,
    accountNumber: mockCustomer.accountNumber,
    currency: mockCustomer.currency,
    outstanding: mockCustomer.outstanding,
    overdue: mockCustomer.overdue,
    creditLimit: mockCustomer.creditLimit,
    currencyCreditLimit: mockCustomer.creditLimit,
    dso: mockCustomer.dso,
    risk: mockCustomer.risk,
    lastPayment: mockCustomer.lastPayment,
    contact: mockCustomer.contact,
    phone: mockCustomer.phone,
    address: mockCustomer.address,
    paymentTerms: mockCustomer.paymentTerms,
    collectionStrategyId: mockCustomer.collectionStrategyId,
    creditControllerId: mockCustomer.creditControllerId || '',
    accountManagerId: mockCustomer.accountManagerId,
    onStop: mockCustomer.onStop ?? false,
    status: mockCustomer.status || 'Active',
    userSortKey1: mockCustomer.userSortKey1 || '',
    userSortKey2: mockCustomer.userSortKey2 || '',
    userSortKey3: mockCustomer.userSortKey3 || '',
    userSortKey4: mockCustomer.userSortKey4 || '',
    userSortKey1Label: mockCustomer.userSortKey1Label || 'User Sort Key 1',
    userSortKey2Label: mockCustomer.userSortKey2Label || 'User Sort Key 2',
    userSortKey3Label: mockCustomer.userSortKey3Label || 'User Sort Key 3',
    userSortKey4Label: mockCustomer.userSortKey4Label || 'User Sort Key 4',
    groupDso: mockCustomer.groupDso,
    isAnyCurrency: mockCustomer.isAnyCurrency,
    transactionCurrencies: mockCustomer.transactionCurrencies,
  };
}

/**
 * Hook to fetch customer details by ID
 * Handles both Demo and Live modes
 */
export function useCustomerDetails(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockCustomer = customerId ? getMockCustomerById(customerId) : null;
  const mockCustomerUI = convertMockCustomerToDetailUI(mockCustomer);

  return useModeAwareQuery<CustomerDetailUI | null, Error>(
    ['customer-detail', customerId],
    async ({ signal }) => {
      if (!customerId) return null;
      const response = await apiGet<CustomerDetailApi>(`/api/v1/customers/${customerId}`, undefined, signal);
      return mapCustomerDetailApiToUi(response);
    },
    mockCustomerUI,
    {
      enabled: !!customerId,
      staleTime: 0, // Always consider data stale to ensure fresh fetches
      refetchOnMount: 'always', // Always refetch when component mounts
    },
  );
}

/**
 * Hook to fetch customer invoices
 * Handles both Demo and Live modes
 */
export function useCustomerInvoices(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockInvoices = customerId ? (mockInvoicesByCustomerId[customerId] ?? []) : [];

  return useModeAwareQuery<InvoiceUI[], Error>(
    ['customer-invoices', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      try {
        const response = await apiGet<OutstandingInvoicesApiResponse | StubResponse>(
          `/api/v1/customers/${customerId}/transactions/outstanding`,
          undefined,
          signal,
        );

        if (isStubResponse(response)) {
          return [];
        }

        return response.invoices.map(mapInvoiceApiToUi);
      } catch (error) {
        console.error('Error fetching outstanding invoices:', error);
        return [];
      }
    },
    mockInvoices,
    {
      enabled: !!customerId,
      staleTime: 0, // Always consider data stale to ensure fresh fetches
      refetchOnMount: 'always', // Always refetch when component mounts
    },
  );
}

/**
 * Hook to fetch customer payments
 * Handles both Demo and Live modes
 */
export function useCustomerPayments(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockPayments = customerId ? (mockPaymentsByCustomerId[customerId] ?? []) : [];

  return useModeAwareQuery<PaymentUI[], Error>(
    ['customer-payments', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      try {
        const response = await apiGet<PaymentHistoryApiResponse | StubResponse>(
          `/api/v1/customers/${customerId}/transactions/payment-history`,
          undefined,
          signal,
        );

        if (isStubResponse(response)) {
          return [];
        }

        return response.payments.map(mapPaymentApiToUi);
      } catch (error) {
        console.error('Error fetching payment history:', error);
        return [];
      }
    },
    mockPayments,
    {
      enabled: !!customerId,
      staleTime: 0, // Always consider data stale to ensure fresh fetches
      refetchOnMount: 'always', // Always refetch when component mounts
    },
  );
}

const ACTIVITY_TYPE_MAP: Record<string, CustomerActivity['type']> = {
  email: 'email',
  phone: 'phone',
  payment: 'payment',
  system: 'system',
  note: 'note',
  promise: 'promise',
  dispute: 'dispute',
  case: 'case',
};

function mapRecentActivityApiToCustomerActivity(item: RecentActivityListItemApiResponse): CustomerActivity {
  const mappedType = ACTIVITY_TYPE_MAP[item.activityType] ?? 'system';

  let metadata: CustomerActivity['metadata'];
  if (item.metadataJson) {
    try {
      metadata = JSON.parse(item.metadataJson) as CustomerActivity['metadata'];
    } catch {
      metadata = undefined;
    }
  }

  return {
    id: String(item.id),
    customerId: String(item.customerId),
    type: mappedType,
    date: item.createdAt,
    actor: item.actorName,
    title: item.title,
    description: item.description,
    metadata,
  };
}

export function useCustomerRecentActivities(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockActivities = customerId ? getMockCustomerActivities(customerId, 10) : [];

  return useModeAwareQuery<CustomerActivity[], Error>(
    ['customer-recent-activities', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      try {
        const response = await apiGet<GetRecentActivitiesApiResponse>(
          `/api/v1/recent-activities/customers/${customerId}`,
          { page: 1, pageSize: 10 },
          signal,
        );
        return response.data.map(mapRecentActivityApiToCustomerActivity);
      } catch (error) {
        console.error('Error fetching customer recent activities:', error);
        return [];
      }
    },
    mockActivities,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
